import { TileMap, TileSize } from "../models"
import { STOREY_RISE_PX } from "./ember/two_storey_house"

/**
 * HILLS: raised ground in ONE room that bodies walk up and down without a jump, a ladder or a room change
 * (openspec/specs/world/hills). The room's data-only `hill` layer holds a whole-number level per cell
 * (TileMapDirectory.hill, column-major like `rise`); this file turns those levels into the height of the ground at
 * any point, in pixels, for everything that draws on a hill -- the hill's own art, and every body, item, overlay and
 * camera lifted by it -- so they agree to the pixel.
 *
 * THE FIELD. For each level L, f_L is the quadratic B-spline of the cells at level L or above, sampled at cell
 * centres: 1 inside the level, 0 well outside, 0.5 on its edge, and smooth (no creases, unlike bilinear). A small
 * world-anchored wobble is added so no edge runs ruler-straight, and each level climbs one storey over
 * smoothstep(0.5 - EDGE, 0.5 + EDGE): a slope about 0.9 of a cell wide, straddling the level's edge, with a
 * stair-stepped edge blending into a diagonal. H = STOREY_RISE_PX x the sum over levels.
 *
 * CLIFFS (openspec world/cliffs). Where the 3x3 cells round a point hold a gap of two levels or more, every level the
 * gap spans turns sheer there, by how sheer the window is at that level (sheerness): it climbs its storey at once a
 * little outside its cells (CLIFF_OUTSET), with a stronger wobble (cliffWobble), instead of over the slope. So beside a
 * cliff the drop is the gap's whole height on one line that wanders and runs smooth past staircases as a slope's edge
 * does, a cliff's end eases into a slope, and the levels either side keep their slopes. Cells that keep the knoll rule
 * never hold such a gap, so their heights are exactly as without cliffs.
 *
 * LOCALITY. The spline reaches exactly 1.5 cells, so a point's height depends only on the 3x3 cells round the cell
 * it is in: chunks baked apart agree at their seams, and an edit to the levels changes heights only within 1.5
 * cells of it.
 *
 * Height is a LOOK, except at a cliff: the shared mover reads it to stop a body at a cliff's face and to let one drop
 * off its rim. Otherwise collision, speed, prediction, combat and AI treat a hill as flat.
 */

/** The highest level a hill cell may hold. */
export const HILL_LEVEL_MAX = 8
/** Half the span of spline values a level climbs over (0.5 -+ this). */
export const HILL_EDGE = 0.35
/** How far the world-anchored noise moves the spline (it is added as WOBBLE x (noise - 0.5)). */
export const HILL_WOBBLE = 0.14

/** A room's hill levels, read off its `hill` layer. */
export interface HillLevels {
    /** Cells across and down. */
    readonly width: number
    readonly height: number
    /** The highest level anywhere on the map, or in the border round it. */
    readonly max: number
    /** The level of cell (cx, cy): the border's past the room's edge, 0 beyond it or where there is no hill. */
    at(cx: number, cy: number): number
    /** Whether two side-by-side cells, the border's included, stand two levels or more apart somewhere: a cliff. */
    readonly cliffs?: boolean
}

/**
 * A seamless room's neighbours' levels round its edge (TileMapDirectory.hillHalo): rows of digits covering the room
 * and `margin` cells past every edge, the cell at (x, y) being `rows[y + margin][x + margin]`. Past a room's edge its
 * height comes from here, so a slope crossing the seam rises the same in both rooms; with none, that ground is flat.
 */
export interface HillHalo {
    margin: number
    rows: string[]
}

/** The levels of a `hill` layer and its border, or undefined when neither holds a hill. */
export const hillLevelsOf = (layer: TileMap | undefined, halo?: HillHalo): HillLevels | undefined => {
    if (!layer?.length && !halo) return undefined
    const width = layer?.length || (halo ? (halo.rows[0]?.length ?? 0) - 2 * halo.margin : 0)
    const height = layer?.[0]?.length ?? (halo ? halo.rows.length - 2 * halo.margin : 0)
    const cells = new Uint8Array(width * height)
    let max = 0
    for (let x = 0; x < width; x++) {
        const column = layer?.[x]
        if (!column) continue
        for (let y = 0; y < height; y++) {
            const level = Math.max(0, Math.min(HILL_LEVEL_MAX, Math.floor(Number(column[y]) || 0)))
            cells[y * width + x] = level
            if (level > max) max = level
        }
    }
    return max || halo ? hillLevelsFromCells(cells, width, height, halo) : undefined
}

/**
 * The levels of a row-major grid of whole levels (a generated map's `hill` layer data) and its border, or undefined
 * when all are 0.
 */
export const hillLevelsFromCells = (cells: ArrayLike<number>, width: number, height: number, halo?: HillHalo): HillLevels | undefined => {
    let max = 0
    for (let i = 0; i < width * height; i++) if (cells[i] > max) max = cells[i]
    // The border past the room's edge only; inside the room the room's own layer holds.
    const margin = halo?.margin ?? 0, span = width + 2 * margin
    const border = new Uint8Array(halo ? span * (height + 2 * margin) : 0)
    if (halo) {
        for (let y = -margin; y < height + margin; y++) for (let x = -margin; x < width + margin; x++) {
            if (x >= 0 && y >= 0 && x < width && y < height) continue
            const level = Math.max(0, Math.min(HILL_LEVEL_MAX, Math.floor(Number(halo.rows[y + margin]?.[x + margin]) || 0)))
            border[(y + margin) * span + x + margin] = level
            if (level > max) max = level
        }
    }
    if (!max) return undefined
    const at = (cx: number, cy: number) => {
        if (cx >= 0 && cy >= 0 && cx < width && cy < height) return cells[cy * width + cx]
        if (cx < -margin || cy < -margin || cx >= width + margin || cy >= height + margin) return 0
        return border[(cy + margin) * span + cx + margin]
    }
    // A cliff: two side-by-side cells within what the room knows (its own and its border's), two levels or more apart.
    let cliffs = false
    for (let y = -margin; y < height + margin && !cliffs; y++) for (let x = -margin; x < width + margin; x++) {
        const level = at(x, y)
        if ((x + 1 < width + margin && Math.abs(level - at(x + 1, y)) >= 2) ||
            (y + 1 < height + margin && Math.abs(level - at(x, y + 1)) >= 2)) { cliffs = true; break }
    }
    return { width, height, max, at, cliffs }
}

/** Levels from rows of digits, one string per row (tests and the showcase builder's plans). */
export const hillLevelsFromRows = (rows: string[]): HillLevels | undefined => {
    const width = Math.max(0, ...rows.map(r => r.length))
    const layer: TileMap = Array.from({ length: width }, (_, x) => rows.map(r => (/[0-9]/.test(r[x] ?? "") ? r[x] : "0")))
    return hillLevelsOf(layer)
}

/** Every cell's eight neighbours are at most one level lower: each level stands a cell in from the one below. */
export const knollRuleHolds = (levels: HillLevels): boolean => {
    for (let cy = 0; cy < levels.height; cy++) {
        for (let cx = 0; cx < levels.width; cx++) {
            const level = levels.at(cx, cy)
            if (level < 2) continue
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
                if (levels.at(cx + dx, cy + dy) < level - 1) return false
            }
        }
    }
    return true
}

/**
 * Whether the ground is level all round cell (cx, cy): the cell and its eight neighbours share one level. On such a cell
 * the height is exactly that level x STOREY_RISE_PX everywhere in it (the spline sums to one over the nine cells, and
 * the wobble cannot move it out of the flat), so whatever stands there has one lift. Trees and buildings need it on a
 * hill; flat ground away from any hill passes, the cells round a hill's foot (where its toe lies) do not.
 */
export const hillLevelAllRound = (levels: HillLevels | undefined, cx: number, cy: number): boolean => {
    if (!levels) return true
    const level = levels.at(cx, cy)
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (levels.at(cx + dx, cy + dy) !== level) return false
    }
    return true
}

/** The quadratic B-spline: support |t| < 1.5, sums to one over integer shifts. */
const bspline = (t: number): number => {
    const a = Math.abs(t)
    if (a <= 0.5) return 0.75 - a * a
    if (a < 1.5) return 0.5 * (1.5 - a) * (1.5 - a)
    return 0
}

const smoothstep = (e0: number, e1: number, x: number): number => {
    const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
    return t * t * (3 - 2 * t)
}

// The wobble: value noise on a 32px lattice that repeats every 128px, from a fixed table, smoothly interpolated.
const NOISE_CELL = 32
const NOISE_SIZE = 4
const NOISE = Array.from({ length: NOISE_SIZE * NOISE_SIZE }, (_, i) => {
    let h = Math.imul(i + 17, 0x9e3779b1) >>> 0
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b) >>> 0
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296
})
// The lattice repeats every NOISE_SIZE (4) cells, so a bitmask wraps it, negatives included.
const lattice = (i: number, j: number) => NOISE[(j & (NOISE_SIZE - 1)) * NOISE_SIZE + (i & (NOISE_SIZE - 1))]

/** The wobble added to every level's spline at world point (x, y). */
export const hillWobble = (x: number, y: number): number => {
    const u = x / NOISE_CELL, v = y / NOISE_CELL
    const i = Math.floor(u), j = Math.floor(v)
    let fu = u - i, fv = v - j
    fu = fu * fu * (3 - 2 * fu)
    fv = fv * fv * (3 - 2 * fv)
    const a = lattice(i, j), b = lattice(i + 1, j), c = lattice(i, j + 1), d = lattice(i + 1, j + 1)
    const top = a + (b - a) * fu, bottom = c + (d - c) * fu
    return HILL_WOBBLE * (top + (bottom - top) * fv - 0.5)
}

const climb = (f: number) => smoothstep(0.5 - HILL_EDGE, 0.5 + HILL_EDGE, f)

/**
 * The wobble of a cliff's line: the slopes' wobble with a second octave half its size, so a cliff runs in gullies and
 * buttresses a few px deep (about 7 px either way) while still repeating every 128 px, so rooms agree at their seams.
 */
export const cliffWobble = (x: number, y: number): number => hillWobble(x, y) + 0.5 * hillWobble(2 * x + 37, 2 * y + 11)

/**
 * How far a cliff's line stands out from its high cells, in spline units (about 4 px along a straight edge): the face
 * comes down to the sea or the sand beside them, not onto a strip of their own ground. At a staircase's convex corners
 * the line still shaves a few px off the high cell, the price of running smoothly past the steps; the hill painter
 * treats a foot there as in the sea.
 */
const CLIFF_OUTSET = 0.13

// The side-by-side pairs of a 3x3 window (indices k = row * 3 + column): across, down, and both diagonals.
const WINDOW_PAIRS: [number, number][] = []
for (let k = 0; k < 9; k++) {
    const r = Math.floor(k / 3), c = k % 3
    for (const [dr, dc] of [[0, 1], [1, 0], [1, 1], [1, -1]]) {
        const rr = r + dr, cc = c + dc
        if (rr <= 2 && cc >= 0 && cc <= 2) WINDOW_PAIRS.push([k, rr * 3 + cc])
    }
}
/** How fast a level turns sheer with the pairs straddling it round a point: whole within half a cell of a cliff. */
const SHEER_GAIN = 2.4

/**
 * How sheer each level is at a point (0 a slope, 1 a cliff), into `out[L]`, from its nine cells' levels and weights: each
 * side-by-side pair in the window two levels or more apart adds the geometric mean of its two cells' weights to every
 * level it straddles. A pair's share fades to nothing as either cell leaves the window, so the sheerness changes
 * smoothly from cell to cell -- no seam where a cliff tapers -- and needs no cell beyond the window (a seamless room's
 * two-cell border is enough).
 */
const sheerness = (level: ArrayLike<number>, weight: ArrayLike<number>, max: number, out: Float64Array): boolean => {
    out.fill(0, 0, max + 1)
    let any = false
    for (const [a, b] of WINDOW_PAIRS) {
        const la = level[a], lb = level[b]
        if (Math.abs(la - lb) < 2) continue
        const share = Math.sqrt(weight[a] * weight[b])
        if (share === 0) continue
        for (let L = Math.min(la, lb) + 1; L <= Math.max(la, lb); L++) out[L] += share
        any = true
    }
    if (any) for (let L = 0; L <= max; L++) out[L] = Math.min(1, SHEER_GAIN * out[L])
    return any
}
const SHEER = new Float64Array(HILL_LEVEL_MAX + 1)

/**
 * Whether level L is sheer where the 3x3 cells round a point hold `window`: a gap of two levels or more spans it, from
 * the highest cell below L to the lowest at or above it. A window keeping the knoll rule never has one.
 */
export const hillCliffLevel = (window: ArrayLike<number>, L: number): boolean => {
    let below = -1, above = Infinity
    for (let k = 0; k < window.length; k++) {
        const l = window[k]
        if (l < L) { if (l > below) below = l } else if (l < above) above = l
    }
    return below >= 0 && above !== Infinity && above - below >= 2
}

/** The ground's height in pixels at world point (x, y): 0 on flat ground, STOREY_RISE_PX a level on a hill's tiers. */
export const hillHeightAt = (levels: HillLevels | undefined, x: number, y: number): number => {
    if (!levels) return 0
    const u = x / TileSize, v = y / TileSize
    const cx = Math.floor(u), cy = Math.floor(v)
    // The nine cells in reach, with their spline weights; then each level's sum.
    let any = false
    const weight: number[] = []
    const level: number[] = []
    for (let dy = -1; dy <= 1; dy++) {
        const wy = bspline(v - (cy + dy + 0.5))
        for (let dx = -1; dx <= 1; dx++) {
            const l = levels.at(cx + dx, cy + dy)
            if (l) any = true
            weight.push(wy * bspline(u - (cx + dx + 0.5)))
            level.push(l)
        }
    }
    if (!any) return 0
    const wobble = hillWobble(x, y)
    // Near a cliff each level blends from climbing over its slope to stepping up sheer, by how sheer it is round the
    // point (sheerness): whole by the cliff, nothing a cell or so from it, smooth between.
    const sheer = levels.cliffs && sheerness(level, weight, levels.max, SHEER)
    const sheerWobble = sheer ? cliffWobble(x, y) : 0
    let height = 0
    for (let L = 1; L <= levels.max; L++) {
        let f = 0
        for (let k = 0; k < 9; k++) if (level[k] >= L) f += weight[k]
        if (f === 0) break
        const w = sheer ? SHEER[L] : 0
        height += w > 0 ? w * (f + sheerWobble >= 0.5 - CLIFF_OUTSET ? 1 : 0) + (1 - w) * climb(f + wobble) : climb(f + wobble)
    }
    return height * STOREY_RISE_PX
}

/** A rectangle of world pixels: left, top, width, height. */
export interface HillRect {
    x: number
    y: number
    w: number
    h: number
}

/**
 * The height at every pixel centre of `rect` (row-major, w x h), as hillHeightAt gives it at (x + 0.5, y + 0.5) --
 * separable: each level's spline is summed along the cell rows once per pixel column, then down the columns, and the
 * wobble is worked out once per pixel, so a chunk's worth costs a few operations a pixel.
 */
export const hillHeights = (levels: HillLevels | undefined, rect: HillRect): Float32Array => {
    const out = new Float32Array(rect.w * rect.h)
    if (!levels) return out
    // Per pixel column (and row): the cell it is in and the weights of the three cells in reach.
    const reach = (from: number, n: number) => {
        const cell = new Int32Array(n)
        const w = new Float64Array(n * 3)
        for (let i = 0; i < n; i++) {
            const u = (from + i + 0.5) / TileSize
            const c = Math.floor(u)
            cell[i] = c
            for (let d = -1; d <= 1; d++) w[i * 3 + d + 1] = bspline(u - (c + d + 0.5))
        }
        return { cell, w }
    }
    const cols = reach(rect.x, rect.w)
    const rows = reach(rect.y, rect.h)
    const cx0 = cols.cell[0] - 1, cy0 = rows.cell[0] - 1
    const cw = cols.cell[rect.w - 1] + 2 - cx0, ch = rows.cell[rect.h - 1] + 2 - cy0
    const cells = new Uint8Array(cw * ch)
    let any = false
    for (let r = 0; r < ch; r++) for (let c = 0; c < cw; c++) {
        const level = levels.at(cx0 + c, cy0 + r)
        cells[r * cw + c] = level
        if (level) any = true
    }
    if (!any) return out
    const along = new Float64Array(ch * rect.w)
    let wobble: Float64Array | undefined
    for (let L = 1; L <= levels.max; L++) {
        let anyAtLevel = false
        // Along each cell row: the spline across the three columns of cells in reach of each pixel column.
        for (let r = 0; r < ch; r++) {
            for (let i = 0; i < rect.w; i++) {
                const c = cols.cell[i] - cx0 + r * cw
                const v = (cells[c - 1] >= L ? cols.w[i * 3] : 0) + (cells[c] >= L ? cols.w[i * 3 + 1] : 0) + (cells[c + 1] >= L ? cols.w[i * 3 + 2] : 0)
                along[r * rect.w + i] = v
                if (v) anyAtLevel = true
            }
        }
        if (!anyAtLevel) break
        // Down each pixel column: the three cell rows in reach.
        for (let j = 0; j < rect.h; j++) {
            const r = rows.cell[j] - cy0
            const w0 = rows.w[j * 3], w1 = rows.w[j * 3 + 1], w2 = rows.w[j * 3 + 2]
            const a0 = (r - 1) * rect.w, a1 = r * rect.w, a2 = (r + 1) * rect.w
            for (let i = 0; i < rect.w; i++) {
                const f = w0 * along[a0 + i] + w1 * along[a1 + i] + w2 * along[a2 + i]
                if (f <= 0) continue
                if (!wobble) {
                    wobble = new Float64Array(rect.w * rect.h).fill(NaN)
                }
                const k = j * rect.w + i
                let wob = wobble[k]
                if (wob !== wob) wob = wobble[k] = hillWobble(rect.x + i + 0.5, rect.y + j + 0.5)
                out[k] += climb(f + wob) * STOREY_RISE_PX
            }
        }
    }
    // Near a cliff the height is not separable (its sheerness pairs the window's cells): those pixels as hillHeightAt.
    if (levels.cliffs) {
        for (let j = 0; j < rect.h; j++) {
            const r = rows.cell[j] - cy0
            for (let i = 0; i < rect.w; i++) {
                const c = cols.cell[i] - cx0
                let lo = Infinity, hi = -Infinity
                for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
                    const l = cells[(r + dr) * cw + c + dc]
                    if (l < lo) lo = l
                    if (l > hi) hi = l
                }
                if (hi - lo >= 2) out[j * rect.w + i] = hillHeightAt(levels, rect.x + i + 0.5, rect.y + j + 0.5)
            }
        }
    }
    return out
}
